import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FormControlLabel, Switch } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import CardGridContainer from "../layouts/CardGridContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import TermCard from "../components/terms/TermCard";
import CreateTermModal from "../components/terms/CreateTermModal";
import UpdateTermModal from "../components/terms/UpdateTermModal";

import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getTerms,
  deleteTerm,
  resetTermServerStatus,
} from "../redux/actions/termActions";
import { TermResponseDto } from "../shared/dtos/term/TermResponseDto";

const TermsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // --- REDUX SELECTORS ---
  const {
    items: terms,
    loadingFetchAll: loadingTerms,
    errorFetchAll: errorLoadingTerms,
    serverStatus,
    loadingCreate: loadingTermCreate,
    errorCreate: errorCreatingTerm,
    loadingUpdate: loadingTermUpdate,
    errorUpdate: errorUpdatingTerm,
    loadingDelete: loadingTermDelete,
  } = useAppSelector((state) => state.term);

  // --- LOCAL FILTER STATE ---
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentOnly, setCurrentOnly] = useState<boolean>(false);

  // --- MODAL STATE ---
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedTerm, setSelectedTerm] = useState<TermResponseDto | null>(
    null,
  );

  // --- PERMISSIONS ---
  const canCreate = hasPermission("term:create");
  const canUpdate = hasPermission("term:update");
  const canDelete = hasPermission("term:delete");

  // --- FETCH DATA ---
  const fetchTerms = useCallback((): void => {
    dispatch(getTerms());
  }, [dispatch]);

  useEffect(() => {
    fetchTerms();
  }, [fetchTerms]);

  // --- FRONTEND FILTERING ---
  const filteredTerms = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return terms.filter((term) => {
      // Current Term switch filter
      if (currentOnly && !term.isCurrentTerm) {
        return false;
      }

      if (!query) return true;

      const matchesName = term.name?.toLowerCase().includes(query);
      const matchesAcademicYear = term.academicYearName
        ?.toLowerCase()
        .includes(query);

      return Boolean(matchesName || matchesAcademicYear);
    });
  }, [terms, searchTerm, currentOnly]);

  // --- MODAL HANDLERS ---
  const handleOpenCreate = (): void => setIsCreateModalOpen(true);

  const handleCloseCreate = useCallback((): void => {
    if (!loadingTermCreate) setIsCreateModalOpen(false);
  }, [loadingTermCreate]);

  const handleOpenUpdate = (term: TermResponseDto): void => {
    setSelectedTerm(term);
    setIsUpdateModalOpen(true);
  };

  const handleCloseUpdate = useCallback((): void => {
    if (!loadingTermUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedTerm(null);
    }
  }, [loadingTermUpdate]);

  const handleOpenDelete = (term: TermResponseDto): void => {
    setSelectedTerm(term);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDelete = useCallback((): void => {
    if (!loadingTermDelete) {
      setIsDeleteModalOpen(false);
      setSelectedTerm(null);
    }
  }, [loadingTermDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedTerm) {
      dispatch(deleteTerm(selectedTerm.id));
    }
  };

  // --- AUTO-CLOSE ON SERVER STATUS ---
  useEffect(() => {
    if (!serverStatus) return;

    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) {
      handleCloseCreate();
    }
    if (operation === "update" && status === 200) {
      handleCloseUpdate();
    }
    if (operation === "delete" && status === 200) {
      handleCloseDelete();
    }

    dispatch(resetTermServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  return (
    <PageContainer
      title="Terms"
      subtitle="Manage academic terms, duration, and current active status."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Add Term
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search terms by name or academic year..."
      >
        <FormControlLabel
          control={
            <Switch
              checked={currentOnly}
              onChange={(e) => setCurrentOnly(e.target.checked)}
              color="primary"
            />
          }
          label="Current Term Only"
          sx={{
            userSelect: "none",
            "& .MuiFormControlLabel-label": {
              fontSize: "0.875rem",
              fontWeight: 500,
            },
          }}
        />
      </PageToolbar>

      <CardGridContainer
        loading={loadingTerms}
        error={errorLoadingTerms}
        itemCount={filteredTerms.length}
        emptyMessage="No terms match your search criteria"
        onRetry={fetchTerms}
      >
        {filteredTerms.map((term) => (
          <TermCard
            key={term.id}
            term={term}
            onEdit={canUpdate ? () => handleOpenUpdate(term) : undefined}
            onDelete={canDelete ? () => handleOpenDelete(term) : undefined}
          />
        ))}
      </CardGridContainer>

      {/* CREATE MODAL */}
      {isCreateModalOpen && (
        <CreateTermModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingTermCreate}
          error={errorCreatingTerm}
        />
      )}

      {/* UPDATE MODAL */}
      {isUpdateModalOpen && selectedTerm && (
        <UpdateTermModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          term={selectedTerm}
          loading={loadingTermUpdate}
          error={errorUpdatingTerm}
        />
      )}

      {/* DELETE CONFIRM DIALOG */}
      {isDeleteModalOpen && selectedTerm && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Term"
          description={`Are you sure you want to delete "${selectedTerm.name}"? This action cannot be undone.`}
          loading={loadingTermDelete}
        />
      )}
    </PageContainer>
  );
};

export default TermsPage;
